import {
  ALL_FEATURES_AVAILABLE,
  SubscriptionPlans,
} from '../../../../../../user/user/user.model';
import { MapModalData } from '../../../../../../user/map-redirect-modal/map-redirect-modal.component';
import { ROUTER_NAVIGATE } from '../../../../../../user/map-redirect-modal/map-redirect-modal-text';

export function generateInaccessibleModalData(
  featureDisplayName: string,
  isUnauthorized: boolean,
  featureConst: string
): MapModalData {
  // For unauthorized users
  if (isUnauthorized) {
    return {
      headerText: `Unlock This with a Free Trial`,
      mainText: `You're using a guest version with limited access.`,
      additionalText:
        'Start your free trial to explore it now — no credit card needed.',
      buttonText: 'Sign Up For Free',
      URLtoGo: '/sign-up',
      actionType: ROUTER_NAVIGATE,
      hasBackdrop: true,
      gaEventOnButtonClick: 'start_free_trial_click',
    };
  }

  // For sign up users determine which plans offer the feature
  const userFacingPlans: SubscriptionPlans[] = [
    SubscriptionPlans.HOME_SEARCH,
    SubscriptionPlans.REAL_ESTATE,
    SubscriptionPlans.SITE_SELECTION,
  ];

  // the one that unlock the feature
  const unlockingPlans = userFacingPlans.filter((planKey) => {
    const plan = SubscriptionPlans.description[planKey];
    return (
      plan.accessibleFeatures == ALL_FEATURES_AVAILABLE ||
      (Array.isArray(plan.accessibleFeatures) &&
        plan.accessibleFeatures.includes(featureConst))
    );
  });

  const baseModal: Omit<MapModalData, 'mainText'> = {
    headerText: `Unlock ${featureDisplayName}`,
    buttonText: 'Get started',
    URLtoGo: '/user/subscription',
    actionType: ROUTER_NAVIGATE,
    hasBackdrop: true,
    gaEventOnButtonClick: 'get_started_locked_param',
  };

  if (unlockingPlans.length !== userFacingPlans.length) {
    const excludedPlans = userFacingPlans
      .filter((p) => !unlockingPlans.includes(p))
      .map((p) => {
        return SubscriptionPlans.description[p].planName;
      })
      .join(' or ');

    return {
      // All but A and B
      ...baseModal,
      mainText: `${featureDisplayName} is available in all subscriptions except ${excludedPlans}.`,
      additionalText: `To access it, upgrade to any other subscription plan.`,
    };
  }

  // Read off the plans themselves rather than written down here: the number
  // was hardcoded and went stale the moment subscriptions were re-priced.
  //
  // Strips everything but digits and the decimal point, so a thousands
  // separator ('$2,499.95', which ENTERPRISE already carries) sorts as 2499
  // and not as 2 the way a bare '$' strip would.
  const amountOf = (price: string): number =>
    parseFloat(price.replace(/[^\d.]/g, ''));

  const lowestPrice = userFacingPlans
    .map((planKey) => SubscriptionPlans.description[planKey].priceMonthly)
    .filter((price): price is string => !!price)
    .sort((a, b) => amountOf(a) - amountOf(b))[0];

  // Default fallback message for all subscriptions
  return {
    ...baseModal,
    mainText: `${featureDisplayName} is available with any subscription.`,
    additionalText: lowestPrice
      ? `Get full access starting ${lowestPrice}/month.`
      : `Get full access with any subscription.`,
  };
}
